import { useQuery, useMutation } from "@tanstack/react-query";
import { useState } from "react";
import { formatLocalDate } from "@/lib/utils";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { VoiceToTextTextarea as Textarea } from "@/components/voice-field";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import {
  DollarSign, Search, X, Loader2, Users, TrendingUp, Clock, Plus,
  MapPin, ChevronDown, ChevronRight, CalendarDays,
} from "lucide-react";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import { useIsMobile } from "@/hooks/use-mobile";
import type { CommissionPayment, UserProfile } from "@shared/schema";

const fmt = (amount: number | string | null | undefined) =>
  new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(
    parseFloat(String(amount || "0"))
  );

const fmtDate = (d: string | Date | null | undefined) =>
  formatLocalDate(d, { year: "numeric", month: "short", day: "numeric" });

const fmtShortDate = (d: string) =>
  new Date(d + "T12:00:00").toLocaleDateString("en-US", { month: "short", day: "numeric" });

// ─── Types for auto-calculated trip commissions ───────────────────────────────
interface TripResult {
  regionId: string | null;
  regionName: string | null;
  eventDates: string[];
  showCount: number;
  totalRevenue: number;
  avgDailyRevenue: number;
  structureName: string;
  structureType: string;
  appliedPercentage: number;
  commissionAmount: number;
  invoiceIds: string[];
}

interface CommissionReport {
  userId: string;
  userName: string;
  trips: TripResult[];
  grandTotalRevenue: number;
  grandTotalCommission: number;
}

// ─── Component ────────────────────────────────────────────────────────────────
export function CommissionsTab() {
  const { toast } = useToast();
  const isMobile = useIsMobile();

  // Date range for auto-calculated section (default: current month)
  const today = new Date();
  const [rangeStart, setRangeStart] = useState(
    new Date(today.getFullYear(), today.getMonth(), 1).toISOString().split("T")[0]
  );
  const [rangeEnd, setRangeEnd] = useState(today.toISOString().split("T")[0]);

  // Payout log section state
  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState<string>("all");
  const [yearFilter, setYearFilter] = useState<string>(new Date().getFullYear().toString());
  const [expandedReps, setExpandedReps] = useState<Set<string>>(new Set());

  // Log Payment dialog
  const [isLogPaymentOpen, setIsLogPaymentOpen] = useState(false);
  const [logPaymentUserId, setLogPaymentUserId] = useState<string>("");
  const [logPaymentAmount, setLogPaymentAmount] = useState("");
  const [logPaymentDate, setLogPaymentDate] = useState(new Date().toISOString().split("T")[0]);
  const [logPaymentNotes, setLogPaymentNotes] = useState("");

  // ── Queries ──────────────────────────────────────────────────────────────────

  // Auto-calculated commissions from trip data
  const { data: reportData = [], isLoading: loadingReport } = useQuery<CommissionReport[]>({
    queryKey: ["/api/billing/commission-report", rangeStart, rangeEnd],
    queryFn: async () => {
      const params = new URLSearchParams({ startDate: rangeStart, endDate: rangeEnd });
      const res = await fetch(`/api/billing/commission-report?${params}`);
      if (!res.ok) throw new Error("Failed to load commission report");
      return res.json();
    },
    staleTime: 60_000,
  });

  // Logged payouts (manual payments recorded)
  const { data: commissionPayments = [], isLoading: loadingPayments } = useQuery<CommissionPayment[]>({
    queryKey: ["/api/billing/commission-payments"],
  });

  const { data: users = [] } = useQuery<(UserProfile & { user?: { firstName: string; lastName: string } })[]>({
    queryKey: ["/api/users-with-profiles"],
  });

  // ── Mutations ────────────────────────────────────────────────────────────────
  const logPaymentMutation = useMutation({
    mutationFn: async (data: { userId: string; amount: string; paymentDate: string; notes: string }) =>
      apiRequest("POST", "/api/billing/commission-payouts", data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/billing/commission-payments"] });
      toast({ title: "Payment Logged", description: "Commission payout recorded." });
      setIsLogPaymentOpen(false);
      resetLogPaymentForm();
    },
    onError: (error: any) => {
      toast({ title: "Error", description: error.message || "Failed to log payment", variant: "destructive" });
    },
  });

  const resetLogPaymentForm = () => {
    setLogPaymentUserId("");
    setLogPaymentAmount("");
    setLogPaymentDate(new Date().toISOString().split("T")[0]);
    setLogPaymentNotes("");
  };

  const handleLogPayment = () => {
    if (!logPaymentUserId) {
      toast({ title: "Error", description: "Please select a recipient", variant: "destructive" });
      return;
    }
    if (!logPaymentAmount || parseFloat(logPaymentAmount) <= 0) {
      toast({ title: "Error", description: "Please enter a valid amount", variant: "destructive" });
      return;
    }
    if (logPaymentUserId === "other" && !logPaymentNotes.trim()) {
      toast({ title: "Error", description: "Notes are required when selecting 'Other'", variant: "destructive" });
      return;
    }
    logPaymentMutation.mutate({
      userId: logPaymentUserId,
      amount: logPaymentAmount,
      paymentDate: logPaymentDate,
      notes: logPaymentNotes,
    });
  };

  // ── Derived values ───────────────────────────────────────────────────────────
  const totalOwed = reportData.reduce((s, r) => s + r.grandTotalCommission, 0);
  const totalRevenue = reportData.reduce((s, r) => s + r.grandTotalRevenue, 0);

  const userMap = new Map(users.map((u) => [u.userId, u]));

  const filteredPayments = commissionPayments.filter((payment) => {
    const user = userMap.get(payment.userId);
    const userName = user?.user ? `${user.user.firstName} ${user.user.lastName}` : payment.userId;
    const matchesSearch = !searchQuery || userName.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesStatus = statusFilter === "all" || payment.status === statusFilter;
    if (yearFilter === "all") return matchesSearch && matchesStatus;
    const paymentDate = payment.createdAt ? new Date(payment.createdAt) : null;
    return matchesSearch && matchesStatus && paymentDate?.getFullYear().toString() === yearFilter;
  });

  const pendingAmount = filteredPayments.filter(p => p.status === "pending").reduce((s, p) => s + parseFloat(p.commissionAmount || "0"), 0);
  const paidAmount   = filteredPayments.filter(p => p.status === "paid").reduce((s, p) => s + parseFloat(p.commissionAmount || "0"), 0);
  const uniqueUsers  = new Set(filteredPayments.map(p => p.userId)).size;

  const toggleRep = (userId: string) => {
    setExpandedReps(prev => {
      const next = new Set(prev);
      next.has(userId) ? next.delete(userId) : next.add(userId);
      return next;
    });
  };

  // ── Render ───────────────────────────────────────────────────────────────────
  return (
    <div className="space-y-6">

      {/* ── SECTION 1: Calculated Commissions ── */}
      <Card>
        <CardHeader className="pb-3">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
            <div>
              <CardTitle className="flex items-center gap-2 text-base">
                <CalendarDays className="w-4 h-4" />
                Calculated Commissions by Trip
              </CardTitle>
              <p className="text-xs text-muted-foreground mt-0.5">
                Auto-calculated from invoices grouped by region. Formula: (sum of show revenue) ÷ (show days) = avg daily → tier % → commission.
              </p>
            </div>
            <div className="flex items-end gap-2 flex-wrap">
              <div className="space-y-1">
                <Label className="text-xs">From</Label>
                <Input
                  type="date"
                  value={rangeStart}
                  onChange={(e) => setRangeStart(e.target.value)}
                  className="w-36 h-8 text-sm"
                  data-testid="input-commission-range-start"
                />
              </div>
              <div className="space-y-1">
                <Label className="text-xs">To</Label>
                <Input
                  type="date"
                  value={rangeEnd}
                  onChange={(e) => setRangeEnd(e.target.value)}
                  className="w-36 h-8 text-sm"
                  data-testid="input-commission-range-end"
                />
              </div>
            </div>
          </div>

          {/* Summary row */}
          {!loadingReport && reportData.length > 0 && (
            <div className="flex gap-4 mt-3 pt-3 border-t text-sm">
              <div>
                <span className="text-muted-foreground">Period Revenue: </span>
                <span className="font-semibold">{fmt(totalRevenue)}</span>
              </div>
              <div>
                <span className="text-muted-foreground">Total Owed: </span>
                <span className="font-semibold text-green-600 dark:text-green-400">{fmt(totalOwed)}</span>
              </div>
            </div>
          )}
        </CardHeader>

        <CardContent className="pt-0">
          {loadingReport ? (
            <div className="flex items-center justify-center py-10">
              <Loader2 className="w-5 h-5 animate-spin text-muted-foreground" />
            </div>
          ) : reportData.length === 0 ? (
            <div className="text-center py-10 text-muted-foreground">
              <MapPin className="h-8 w-8 mx-auto mb-3 opacity-40" />
              <p className="text-sm">No invoices found in this date range with commission structures assigned.</p>
              <p className="text-xs mt-1">Assign commission structures to sales reps in Admin → Commissions, then ensure invoices exist for this period.</p>
            </div>
          ) : (
            <div className="space-y-3">
              {reportData.map((rep) => {
                const isExpanded = expandedReps.has(rep.userId);
                return (
                  <Collapsible key={rep.userId} open={isExpanded} onOpenChange={() => toggleRep(rep.userId)}>
                    <CollapsibleTrigger asChild>
                      <button
                        className="w-full flex items-center justify-between rounded-lg border bg-muted/30 hover:bg-muted/50 px-4 py-3 text-left transition-colors"
                        data-testid={`button-expand-rep-${rep.userId}`}
                      >
                        <div className="flex items-center gap-3">
                          {isExpanded ? <ChevronDown className="w-4 h-4 text-muted-foreground" /> : <ChevronRight className="w-4 h-4 text-muted-foreground" />}
                          <span className="font-medium text-sm">{rep.userName}</span>
                          <Badge variant="outline" className="text-xs">
                            {rep.trips.length} {rep.trips.length === 1 ? "trip" : "trips"}
                          </Badge>
                        </div>
                        <div className="flex items-center gap-6 text-sm">
                          <div className="text-right hidden sm:block">
                            <p className="text-xs text-muted-foreground">Revenue</p>
                            <p className="font-medium">{fmt(rep.grandTotalRevenue)}</p>
                          </div>
                          <div className="text-right">
                            <p className="text-xs text-muted-foreground">Commission</p>
                            <p className="font-semibold text-green-600 dark:text-green-400">{fmt(rep.grandTotalCommission)}</p>
                          </div>
                        </div>
                      </button>
                    </CollapsibleTrigger>
                    <CollapsibleContent>
                      <div className="mt-1 rounded-lg border overflow-hidden">
                        <Table>
                          <TableHeader>
                            <TableRow className="bg-muted/20">
                              <TableHead className="text-xs">Region / Trip</TableHead>
                              <TableHead className="text-xs text-center">Show Days</TableHead>
                              <TableHead className="text-xs text-right">Total Revenue</TableHead>
                              <TableHead className="text-xs text-right">Avg Daily</TableHead>
                              <TableHead className="text-xs text-center">Rate</TableHead>
                              <TableHead className="text-xs text-right font-medium text-foreground">Commission</TableHead>
                            </TableRow>
                          </TableHeader>
                          <TableBody>
                            {rep.trips.map((trip, i) => (
                              <TableRow key={i} data-testid={`row-trip-${rep.userId}-${i}`}>
                                <TableCell>
                                  <div>
                                    <p className="font-medium text-sm flex items-center gap-1.5">
                                      <MapPin className="w-3 h-3 text-muted-foreground" />
                                      {trip.regionName || "No Region"}
                                    </p>
                                    {trip.eventDates.length > 0 && (
                                      <p className="text-xs text-muted-foreground mt-0.5">
                                        {fmtShortDate(trip.eventDates[0])}
                                        {trip.eventDates.length > 1 && (
                                          <> – {fmtShortDate(trip.eventDates[trip.eventDates.length - 1])}</>
                                        )}
                                        {" · "}{trip.showCount} {trip.showCount === 1 ? "show" : "shows"}
                                      </p>
                                    )}
                                  </div>
                                </TableCell>
                                <TableCell className="text-center text-sm font-medium">
                                  {trip.eventDates.length}
                                </TableCell>
                                <TableCell className="text-right text-sm">{fmt(trip.totalRevenue)}</TableCell>
                                <TableCell className="text-right text-sm text-muted-foreground">{fmt(trip.avgDailyRevenue)}</TableCell>
                                <TableCell className="text-center">
                                  <Badge variant={trip.appliedPercentage > 0 ? "default" : "secondary"} className="text-xs">
                                    {trip.appliedPercentage}%
                                  </Badge>
                                </TableCell>
                                <TableCell className="text-right font-semibold text-sm text-green-600 dark:text-green-400">
                                  {fmt(trip.commissionAmount)}
                                </TableCell>
                              </TableRow>
                            ))}
                            {/* Rep total */}
                            <TableRow className="border-t-2 bg-muted/20 font-semibold">
                              <TableCell colSpan={5} className="text-right pr-4 text-sm">Total for {rep.userName}</TableCell>
                              <TableCell className="text-right font-bold text-sm text-green-600 dark:text-green-400">
                                {fmt(rep.grandTotalCommission)}
                              </TableCell>
                            </TableRow>
                          </TableBody>
                        </Table>
                      </div>
                    </CollapsibleContent>
                  </Collapsible>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>

      <Separator />

      {/* ── SECTION 2: Logged Payouts ── */}
      <div className="grid gap-4 md:grid-cols-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between gap-2 space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Logged</CardTitle>
            <DollarSign className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold" data-testid="text-total-commissions">
              {fmt(filteredPayments.reduce((s, p) => s + parseFloat(p.commissionAmount || "0"), 0))}
            </div>
            <p className="text-xs text-muted-foreground">{filteredPayments.length} entries</p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between gap-2 space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Pending Payout</CardTitle>
            <Clock className="h-4 w-4 text-orange-600" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-orange-600" data-testid="text-pending-commissions">{fmt(pendingAmount)}</div>
            <p className="text-xs text-muted-foreground">{filteredPayments.filter(p => p.status === "pending").length} pending</p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between gap-2 space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Paid Out</CardTitle>
            <TrendingUp className="h-4 w-4 text-green-600" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-green-600" data-testid="text-paid-commissions">{fmt(paidAmount)}</div>
            <p className="text-xs text-muted-foreground">{filteredPayments.filter(p => p.status === "paid").length} paid</p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between gap-2 space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Active Reps</CardTitle>
            <Users className="h-4 w-4 text-blue-600" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-blue-600" data-testid="text-active-reps">{uniqueUsers}</div>
            <p className="text-xs text-muted-foreground">with payouts</p>
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
          <div>
            <CardTitle>Logged Payouts</CardTitle>
            <p className="text-sm text-muted-foreground mt-1">Commission payments that have been manually recorded</p>
          </div>
          <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-9 w-full sm:w-48"
                data-testid="input-search-commissions"
              />
              {searchQuery && (
                <Button variant="ghost" size="icon" className="absolute right-1 top-1/2 -translate-y-1/2 h-6 w-6 no-default-hover-elevate" onClick={() => setSearchQuery("")}>
                  <X className="h-3 w-3" />
                </Button>
              )}
            </div>
            <div className="flex gap-2">
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger className="flex-1 sm:w-[120px]" data-testid="select-commissions-status">
                  <SelectValue placeholder="Status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Status</SelectItem>
                  <SelectItem value="pending">Pending</SelectItem>
                  <SelectItem value="paid">Paid</SelectItem>
                </SelectContent>
              </Select>
              <Select value={yearFilter} onValueChange={setYearFilter}>
                <SelectTrigger className="flex-1 sm:w-[110px]" data-testid="select-commissions-year">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Years</SelectItem>
                  <SelectItem value="2026">2026</SelectItem>
                  <SelectItem value="2025">2025</SelectItem>
                  <SelectItem value="2024">2024</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <Button onClick={() => setIsLogPaymentOpen(true)} data-testid="button-log-commission-payment" className="w-full sm:w-auto">
              <Plus className="h-4 w-4 mr-2" />
              Log Payment
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {loadingPayments ? (
            <div className="flex items-center justify-center py-10">
              <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
            </div>
          ) : filteredPayments.length === 0 ? (
            <div className="text-center py-12 text-muted-foreground">
              <DollarSign className="h-10 w-10 mx-auto mb-3 opacity-40" />
              <p className="text-sm">No logged payouts found</p>
              <p className="text-xs mt-1">Use "Log Payment" to record when you pay out a commission</p>
            </div>
          ) : isMobile ? (
            <div className="space-y-2">
              {filteredPayments.map((payment) => {
                const user = userMap.get(payment.userId);
                const userName = user?.user ? `${user.user.firstName} ${user.user.lastName}` : "Unknown";
                return (
                  <div key={payment.id} className="rounded-lg border p-3" data-testid={`row-commission-${payment.id}`}>
                    <div className="flex items-center justify-between mb-1">
                      <span className="font-medium text-sm">{userName}</span>
                      <span className="text-green-600 font-bold">{fmt(payment.commissionAmount)}</span>
                    </div>
                    <div className="flex items-center gap-2 text-xs text-muted-foreground mb-1.5">
                      <span>{fmt(payment.invoiceAmount)}</span>
                      <span>·</span>
                      <span>{parseFloat(payment.calculatedTierPercentage || "0").toFixed(1)}%</span>
                      <span>·</span>
                      <span>{fmtDate(payment.createdAt)}</span>
                    </div>
                    <Badge variant={payment.status === "paid" ? "default" : "secondary"} className="text-xs">{payment.status}</Badge>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Sales Rep</TableHead>
                    <TableHead>Invoice Amount</TableHead>
                    <TableHead>Commission Rate</TableHead>
                    <TableHead>Commission Amount</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Date</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredPayments.map((payment) => {
                    const user = userMap.get(payment.userId);
                    const userName = user?.user ? `${user.user.firstName} ${user.user.lastName}` : "Unknown";
                    return (
                      <TableRow key={payment.id} data-testid={`row-commission-${payment.id}`}>
                        <TableCell className="font-medium">{userName}</TableCell>
                        <TableCell>{fmt(payment.invoiceAmount)}</TableCell>
                        <TableCell><Badge variant="outline">{parseFloat(payment.calculatedTierPercentage || "0").toFixed(1)}%</Badge></TableCell>
                        <TableCell className="font-medium text-green-600">{fmt(payment.commissionAmount)}</TableCell>
                        <TableCell><Badge variant={payment.status === "paid" ? "default" : "secondary"}>{payment.status}</Badge></TableCell>
                        <TableCell>{fmtDate(payment.createdAt)}</TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* ── Log Payment Dialog ── */}
      <Dialog open={isLogPaymentOpen} onOpenChange={(open) => { setIsLogPaymentOpen(open); if (!open) resetLogPaymentForm(); }}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Log Commission Payment</DialogTitle>
            <DialogDescription>Record a commission payout to a sales rep or other recipient.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="log-payment-recipient">Recipient</Label>
              <Select value={logPaymentUserId} onValueChange={setLogPaymentUserId}>
                <SelectTrigger id="log-payment-recipient" data-testid="select-log-payment-recipient">
                  <SelectValue placeholder="Select recipient..." />
                </SelectTrigger>
                <SelectContent>
                  {users.map((user) => (
                    <SelectItem key={user.userId} value={user.userId}>
                      {user.user ? `${user.user.firstName} ${user.user.lastName}` : user.userId}
                    </SelectItem>
                  ))}
                  <SelectItem value="other">Other</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label htmlFor="log-payment-amount">Amount ($)</Label>
              <Input id="log-payment-amount" type="number" step="0.01" min="0" placeholder="0.00" value={logPaymentAmount} onChange={(e) => setLogPaymentAmount(e.target.value)} data-testid="input-log-payment-amount" />
            </div>
            <div className="space-y-2">
              <Label htmlFor="log-payment-date">Payment Date</Label>
              <Input id="log-payment-date" type="date" value={logPaymentDate} onChange={(e) => setLogPaymentDate(e.target.value)} data-testid="input-log-payment-date" />
            </div>
            <div className="space-y-2">
              <Label htmlFor="log-payment-notes">Notes {logPaymentUserId === "other" && <span className="text-destructive">*</span>}</Label>
              <Textarea id="log-payment-notes" placeholder={logPaymentUserId === "other" ? "Required - describe the recipient and purpose..." : "Optional notes..."} value={logPaymentNotes} onChange={(e) => setLogPaymentNotes(e.target.value)} className="min-h-[80px]" data-testid="input-log-payment-notes" />
              {logPaymentUserId === "other" && <p className="text-xs text-muted-foreground">Notes are required when selecting "Other".</p>}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsLogPaymentOpen(false)} data-testid="button-cancel-log-payment">Cancel</Button>
            <Button onClick={handleLogPayment} disabled={logPaymentMutation.isPending} data-testid="button-confirm-log-payment">
              {logPaymentMutation.isPending ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Logging...</> : "Log Payment"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
